// components/PortfolioAnalyzer.jsx
import React, { useState } from 'react';
import '../../styles/PortfolioAnalyzer.css';

export const PortfolioAnalyzer = ({ onAnalyze, portfolioId }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    setLoading(true);
    const result = await onAnalyze();
    
    // Mock analysis data
    setAnalysis({
      timestamp: new Date().toLocaleTimeString(),
      portfolioValue: 1250000,
      totalPositions: 8,
      riskScore: 65,
      diversificationScore: 72,
      concentrationRisk: 'Moderate',
      var95: 45000,
      expectedShortfall: 62000,
      largestPosition: {
        symbol: 'AAPL',
        value: 150000,
        allocation: 12,
        beta: 1.1
      },
      sectorAllocation: [
        { sector: 'Technology', allocation: 42, risk: 'High' },
        { sector: 'Bonds', allocation: 16, risk: 'Low' },
        { sector: 'Energy', allocation: 12, risk: 'Medium' },
        { sector: 'Healthcare', allocation: 10, risk: 'Medium' },
        { sector: 'Consumer', allocation: 8, risk: 'Low' },
        { sector: 'Other', allocation: 12, risk: 'High' }
      ],
      recommendations: [
        'Reduce technology sector exposure by 10%',
        'Consider adding more fixed income for diversification',
        'Review position sizing for high-beta stocks',
        'Implement tighter stop losses on volatile positions'
      ]
    });
    setLoading(false);
  };

  return (
    <div className="panel portfolio-analyzer">
      <div className="panel-header">
        <h2>Portfolio Analysis</h2>
        <span className="api-endpoint">GET /rms/portfolio/analyze/{portfolioId}</span>
      </div>

      <div className="panel-content">
        <div className="analyze-controls">
          <div className="portfolio-info-card">
            <div className="info-row">
              <span className="info-label">Portfolio ID:</span>
              <span className="info-value">{portfolioId}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="info-badge active">Active</span>
            </div>
            <div className="info-row">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">Today, 09:45 AM</span>
            </div>
          </div>

          <button 
            className="btn btn-primary analyze-btn"
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-small"></div>
                Analyzing...
              </>
            ) : (
              <>
                📊 Analyze Portfolio
              </>
            )}
          </button>
        </div>

        {analysis && (
          <div className="analysis-results">
            <div className="results-header">
              <h3>Analysis Results</h3>
              <span className="timestamp">{analysis.timestamp}</span>
            </div>

            <div className="key-metrics">
              <div className="metric-card">
                <div className="metric-label">Portfolio Value</div>
                <div className="metric-value">
                  ${analysis.portfolioValue.toLocaleString()}
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-label">Risk Score</div>
                <div className="metric-value">
                  <div className="score-display">
                    <div className="score-circle" style={{ 
                      '--score': analysis.riskScore,
                      '--color': analysis.riskScore > 70 ? 'var(--danger-color)' : 
                                analysis.riskScore > 40 ? 'var(--warning-color)' : 
                                'var(--success-color)'
                    }}>
                      <span>{analysis.riskScore}</span>
                    </div>
                    <span className="score-label">/100</span>
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-label">VaR (95%)</div>
                <div className="metric-value danger">
                  ${analysis.var95.toLocaleString()}
                </div>
                <div className="metric-subtext">1-day Value at Risk</div>
              </div>
              
              <div className="metric-card">
                <div className="metric-label">Positions</div>
                <div className="metric-value">{analysis.totalPositions}</div>
                <div className="metric-subtext">Total holdings</div>
              </div>
            </div>

            <div className="detailed-analysis">
              <div className="analysis-section">
                <h4>Sector Allocation & Risk</h4>
                <div className="sector-allocation">
                  {analysis.sectorAllocation.map((sector, index) => (
                    <div key={index} className="sector-item">
                      <div className="sector-header">
                        <span className="sector-name">{sector.sector}</span>
                        <span className="sector-allocation-value">{sector.allocation}%</span>
                      </div>
                      <div className="allocation-bar">
                        <div 
                          className="allocation-fill"
                          style={{ width: `${sector.allocation}%` }}
                          data-risk={sector.risk.toLowerCase()}
                        />
                      </div>
                      <div className="sector-risk">
                        <span className={`risk-badge ${sector.risk.toLowerCase()}`}>
                          {sector.risk} Risk
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analysis-section">
                <h4>Concentration Analysis</h4>
                <div className="concentration-info">
                  <div className="concentration-item">
                    <span className="concentration-label">Largest Position:</span>
                    <span className="concentration-value">{analysis.largestPosition.symbol}</span>
                  </div>
                  <div className="concentration-item">
                    <span className="concentration-label">Value:</span>
                    <span className="concentration-value">
                      ${analysis.largestPosition.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="concentration-item">
                    <span className="concentration-label">Allocation:</span>
                    <span className="concentration-value">{analysis.largestPosition.allocation}%</span>
                  </div>
                  <div className="concentration-item">
                    <span className="concentration-label">Beta:</span>
                    <span className="concentration-value">{analysis.largestPosition.beta}</span>
                  </div>
                </div>
              </div>

              <div className="analysis-section">
                <h4>Risk Recommendations</h4>
                <div className="recommendations">
                  {analysis.recommendations.map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <span className="rec-number">{index + 1}</span>
                      <span className="rec-text">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="actions">
              <button className="btn btn-secondary">
                📥 Download Report
              </button>
              <button className="btn btn-primary">
                🔄 Re-analyze
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};